import { FC } from 'react';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import FormError from 'dashboard/components/FormControls/FormError';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { WorkflowModulePayload } from 'dashboard/models/workflow-module';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import TextArea from 'design-system/components/TextArea';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface CreateWorkflowModalProps {
  handleClose: () => void;
  onSubmit: (workflowModulePayload: WorkflowModulePayload) => void;
}

interface CreateModuleData {
  name: string;
  description: string;
}

const CreateWorkflowModal: FC<CreateWorkflowModalProps> = ({ handleClose, onSubmit }) => {
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useFormSecurely<CreateModuleData>({
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const submit = (data: CreateModuleData) => {
    onSubmit({
      type: ApiModel.WorkflowModule,
      attributes: {
        name: data.name,
        description: data.description,
      },
    });
  };

  return (
    <ModalContainer>
      <ModalHeader onClose={handleClose}>New Module</ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit(submit)}>
          <FormGroup>
            <FormControlLabel htmlFor='create-module-modal__input-name'>Name</FormControlLabel>
            <Input
              {...register('name', {
                required: 'Please give your module a name',
                maxLength: { value: 255, message: 'Module name must not exceed 255 characters' },
              })}
              id='create-module-modal__input-name'
              hasError={!!errors.name}
            />
            {errors.name && <FormError>{errors.name.message}</FormError>}
            <Spacer height={2} />
            <FormControlLabel htmlFor='create-module-modal__input-description'>
              Description
            </FormControlLabel>
            <TextArea
              {...register('description')}
              id='create-module-modal__input-description'
              placeholder='Optional description for this module...'
            />
          </FormGroup>
          <Button
            type='submit'
            width='auto'
            id='create-module-modal__submit'
            isPending={isSubmitting}
          >
            Continue
          </Button>
        </Form>
      </ModalBody>
    </ModalContainer>
  );
};

export default CreateWorkflowModal;
